import { Call } from 'starknet';
import { getConfigManager } from '../../config/getConfigManager';
import { Decimal } from '../../datastructures';
import { MultiCallContract } from '../../services';
import { MULTICALL_CONTRACT_ADDRESS } from '../../constants';
import poolTokenRatesAtom from './poolTokenRatesAtom';
import { createAsyncAtom } from '../../lib/state';
import rpcProviderAtom from '../rpcProviderAtom';

const RESPONSE_CHUNK_SIZE = 5;
const poolsTvlAtom = createAsyncAtom<{
  [key: string]: { tokenA: Decimal; tokenB: Decimal; tokenAUsd: Decimal; tokenBUsd: Decimal };
}>(
  async get => {
    const pools = getConfigManager().getConfig().pools;

    const poolTokenRates = await get(poolTokenRatesAtom);
    const provider = await get(rpcProviderAtom);

    // Token rates not yet loaded, abort tvl fetch until rates are loaded
    if (Object.keys(poolTokenRates).length === 0) {
      return {};
    }

    const poolsTvlMap: { [key: string]: { tokenA: Decimal; tokenB: Decimal; tokenAUsd: Decimal; tokenBUsd: Decimal } } =
      {};

    const calldata: Call[] = Object.entries(pools).map(([, poolConfig]) => ({
      contractAddress: poolConfig.pair.address,
      entrypoint: 'get_reserves',
      calldata: [],
    }));

    const multiCallContract = new MultiCallContract(MULTICALL_CONTRACT_ADDRESS, provider);

    const result = await multiCallContract.aggregate(calldata);
    if (!result) {
      return {};
    }

    for (let i = 0; i < result.length; i += RESPONSE_CHUNK_SIZE) {
      const [, poolsConfig] = Object.entries(pools)[i / RESPONSE_CHUNK_SIZE];

      poolsTvlMap[poolsConfig.pair.id] = {
        tokenA: Decimal.ZERO,
        tokenB: Decimal.ZERO,
        tokenAUsd: Decimal.ZERO,
        tokenBUsd: Decimal.ZERO,
      };

      const tokenARate = poolTokenRates[poolsConfig.pair.tokenA.address];
      const tokenBRate = poolTokenRates[poolsConfig.pair.tokenB.address];

      if (!tokenARate || !tokenBRate) {
        return {};
      }

      // Index 1,2 is tokenA reserves
      const tokenAReserves = new Decimal({ low: result[i + 1], high: result[i + 2] }, poolsConfig.pair.tokenA.decimals);
      // index 3,4 is tokenB reserves
      const tokenBReserves = new Decimal({ low: result[i + 3], high: result[i + 4] }, poolsConfig.pair.tokenB.decimals);

      poolsTvlMap[poolsConfig.pair.id].tokenA = tokenAReserves;
      poolsTvlMap[poolsConfig.pair.id].tokenB = tokenBReserves;
      poolsTvlMap[poolsConfig.pair.id].tokenAUsd = tokenAReserves.mul(tokenARate);
      poolsTvlMap[poolsConfig.pair.id].tokenBUsd = tokenBReserves.mul(tokenBRate);
    }

    return poolsTvlMap;
  },
  10000,
  ['pool-deposit', 'pool-withdraw'],
);

export default poolsTvlAtom;
